// Pure helpers for the Edit Field "Meteorological Data" station list.
// Kept dependency-free (no React / webpack aliases) so they can be unit-tested
// in isolation with `node --test`.
//
// The list being edited is geo_data.meteorological_stations: internal station
// IDs as strings. Its order is the priority the backend reads the stations in,
// and it may hold the same ID more than once.

// Append a station ID (as a string) unless it is already listed.
const addStation = (stationIds, stationId) => {
  const current = stationIds || [];
  const id = String(stationId);
  if (current.map(String).includes(id)) return [...current];
  return [...current, id];
};

// Remove the entry at `index`. By index rather than by ID, so that one of two
// duplicate entries can be removed on its own.
const removeStationAt = (stationIds, index) => {
  const current = stationIds || [];
  return current.filter((_, i) => i !== index);
};

// Move the entry at `from` so it ends up at index `to` (the list order is the
// priority the backend reads stations in). Out-of-range or no-op moves return
// an unchanged copy.
const moveStation = (stationIds, from, to) => {
  const current = [...(stationIds || [])];
  const inRange = (i) => Number.isInteger(i) && i >= 0 && i < current.length;
  if (!inRange(from) || !inRange(to) || from === to) return current;
  const [moved] = current.splice(from, 1);
  current.splice(to, 0, moved);
  return current;
};

// One display row per listed ID, in order.
//
//   availableStations     - the station catalogue ({ id, name }), or
//                           undefined/null while it is still loading
//   meteorologicalSources - station names the backend sent with the field
//                           (geo_data.meteorological_sources); they describe
//                           the SAVED list, so they are only a fallback label
//                           while the catalogue loads, and only when they line
//                           up one-to-one with the IDs
const buildStationRows = (
  stationIds,
  availableStations,
  meteorologicalSources,
) => {
  const ids = stationIds || [];
  const sources = meteorologicalSources || [];
  const useSources = !availableStations && sources.length === ids.length;

  return ids.map((stationId, index) => {
    const id = String(stationId);
    const station = (availableStations || []).find((s) => String(s.id) === id);
    const label =
      (station && (station.display_name || station.name)) ||
      (useSources && sources[index]);
    return { id, index, label: label || id };
  });
};

// A station is offered unless it was checked and found not reporting. Stations
// the backend never checked (active undefined/null) stay pickable.
const isPickable = (station) => station.active !== false;

const typeOf = (station) => station.source_type || station.source || '';

const byStationNumber = (a, b) =>
  String(a.station_id).localeCompare(String(b.station_id), undefined, {
    numeric: true,
  });

// Station types (e.g. "agrometeo", "meteotech") that still have a pickable
// station not already on the field, sorted alphabetically.
const pickableStationTypes = (availableStations, stationIds) => {
  const types = new Set(
    pickableStations(availableStations, stationIds).map(typeOf),
  );
  return [...types].filter(Boolean).sort((a, b) => a.localeCompare(b));
};

// Active stations not already on the field, optionally of one type, ordered by
// station number. Each keeps `label` ("676 · בית ציידה") for the dropdown.
const pickableStations = (availableStations, stationIds, stationType) => {
  const taken = (stationIds || []).map(String);
  return (availableStations || [])
    .filter(isPickable)
    .filter((station) => !taken.includes(String(station.id)))
    .filter((station) => !stationType || typeOf(station) === stationType)
    .map((station) => ({
      ...station,
      label: station.label || String(station.station_id ?? station.name),
    }))
    .sort(byStationNumber);
};

// Furthest a station may be from the field and still count as "nearest".
const NEAREST_STATION_MAX_KM = 50;

// Mean of every vertex of the field polygon(s) (geo_data.areas: a list of
// rings of { latitude, longitude }), or null when the field has none.
const fieldCenter = (areas) => {
  const points = (areas || [])
    .flat()
    .filter(
      (p) => p && Number.isFinite(+p.latitude) && Number.isFinite(+p.longitude),
    );
  if (points.length === 0) return null;
  const sum = points.reduce(
    (acc, p) => ({ lat: acc.lat + +p.latitude, lon: acc.lon + +p.longitude }),
    { lat: 0, lon: 0 },
  );
  return { lat: sum.lat / points.length, lon: sum.lon / points.length };
};

// Great-circle (haversine) distance in km.
const distanceKm = (a, b) => {
  const rad = (deg) => (deg * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
};

const hasLocation = (station) =>
  station.lat != null &&
  station.lon != null &&
  Number.isFinite(+station.lat) &&
  Number.isFinite(+station.lon);

// The pickable station (active, not already on the field, with a location)
// closest to the centre of the field polygon, as { station, km }. Null when
// the field has no polygon or no such station is within maxKm.
const nearestStation = (
  availableStations,
  stationIds,
  areas,
  maxKm = NEAREST_STATION_MAX_KM,
) => {
  const center = fieldCenter(areas);
  if (!center) return null;
  return pickableStations(availableStations, stationIds)
    .filter(hasLocation)
    .map((station) => ({
      station,
      km: distanceKm(center, { lat: +station.lat, lon: +station.lon }),
    }))
    .filter(({ km }) => km <= maxKm)
    .reduce((best, cur) => (!best || cur.km < best.km ? cur : best), null);
};

// Station type for display: "field_climate" -> "Field Climate".
const formatStationType = (stationType) =>
  String(stationType || '')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// A row label like "agrometeo: 676 · בית ציידה" with its type part formatted.
const formatStationLabel = (label) => {
  const text = String(label ?? '');
  const sep = text.indexOf(': ');
  if (sep < 0) return text;
  return formatStationType(text.slice(0, sep)) + text.slice(sep);
};

module.exports = {
  formatStationType,
  formatStationLabel,
  typeOf,
  NEAREST_STATION_MAX_KM,
  fieldCenter,
  nearestStation,
  addStation,
  removeStationAt,
  moveStation,
  buildStationRows,
  pickableStationTypes,
  pickableStations,
};
